import { useTranslation } from "react-i18next";
import { Label, Input } from "@tellescope/ui";
import { Select } from "@tellescope/design-system";
import type { FileListBlock } from "../../../../../shared/schema";
import { LayoutSection } from "../shared/LayoutSection";
import { VisibilitySection } from "../shared/VisibilitySection";
import { ItemFilterSection } from "../shared/ItemFilterSection";
import type { BlockTypeEditorProps } from "../types";
import type { VisibilityCondition } from "../../../../../shared/schema/visibility";

type FileListBlockEditorProps = BlockTypeEditorProps<FileListBlock>;

export function FileListBlockEditor({
  block,
  updateProps,
  updateLayout,
  updateVisibility,
  layoutOpen,
  setLayoutOpen,
  visibilityOpen,
  setVisibilityOpen,
}: FileListBlockEditorProps) {
  const { t } = useTranslation();

  const updateItemFilter = (condition: VisibilityCondition | undefined) => {
    if (condition) {
      updateProps({ itemFilter: { condition } });
    } else {
      updateProps({ itemFilter: undefined });
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>{t("Max Items to Show")}</Label>
        <Select
          value={String(block.props.maxItems ?? 10)}
          onValueChange={(value) => updateProps({ maxItems: parseInt(value, 10) })}
        >
          <Select.Trigger>
            <Select.Value />
          </Select.Trigger>
          <Select.Content>
            <Select.Item value="1">1</Select.Item>
            <Select.Item value="2">2</Select.Item>
            <Select.Item value="3">3</Select.Item>
            <Select.Item value="5">5</Select.Item>
            <Select.Item value="10">10</Select.Item>
            <Select.Item value="20">20</Select.Item>
          </Select.Content>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>{t("Empty Message (optional)")}</Label>
        <Input
          value={block.props.emptyMessage || ""}
          onChange={(e) => updateProps({ emptyMessage: e.target.value || undefined })}
          placeholder={t("No files available")}
        />
      </div>

      <ItemFilterSection
        itemFilter={block.props.itemFilter}
        onChange={updateItemFilter}
        description={t("Filter which files are displayed based on conditions. Use 'Files' source for file properties.")}
      />

      <LayoutSection layout={block.layout} onChange={updateLayout} open={layoutOpen} onOpenChange={setLayoutOpen} />
      <VisibilitySection
        visibility={block.visibility}
        onChange={updateVisibility}
        open={visibilityOpen}
        onOpenChange={setVisibilityOpen}
      />
    </div>
  );
}
